import React from 'react';
import {
  Container, Typography, Grid, Card, CardContent, CardMedia, Button,
  Pagination, Box, Chip, CircularProgress, Alert, Tabs, Tab, Paper,
  FormControl, InputLabel, Select, MenuItem, TextField, Snackbar,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Popover,
  Checkbox, FormControlLabel,
} from '@mui/material';
import {
  KeyboardArrowDown, ViewList, ExpandMore, OpenInNew, ArrowUpward,
  ArrowDownward, ChevronLeft, ChevronRight,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'date-fns';
import BlogDialog from '../../components/BlogDialog';
import { useBlogContext } from '../BlogContext';
import { mapIdToShortString, getDaysInMonth, getFirstDayOfMonth, formatDateDisplay, formatDateRange, formatDateForInput, isSameDay, isDateInRange } from './blogUtils';

const DateRangePicker = () => {
  const {
    isAdmin, posts, page, totalPages, loading, error, selectedTag,
    setSelectedTag, dialogOpen, setDialogOpen, dialogMode, selectedPost,
    tabValue, setTabValue, categories, preBuiltCategory, preBuiltSubcategory,
    rankingCriteria, setRankingCriteria, preBuiltLoading, mergedBlogs,
    loadingMergedBlogs, blogPosts, blogPerformanceData, mergedBlogPerformanceData,
    blogPageStats, sourceBlogMap, pausedBlogs, expandedStatsDialog,
    setExpandedStatsDialog, mergedBlogsLimit, setMergedBlogsLimit,
    blogsPerMergedBlogLimit, setBlogsPerMergedBlogLimit,
    mergedBlogsStartDate, mergedBlogsEndDate,
    dateRangePickerOpen, setDateRangePickerOpen,
    dateRangePickerAnchor, setDateRangePickerAnchor,
    tempStartDate, setTempStartDate, tempEndDate, setTempEndDate,
    startMonth, setStartMonth, endMonth, setEndMonth,
    includeToday, setIncludeToday, snackbar, setSnackbar,
    handleCloseSnackbar, handlePageChange, handleTagClick,
    handleCreatePost, handleViewPost, handleEditPost,
    handleCloseDialog, handleDialogSuccess,
    handlePreBuiltCategoryChange, handlePreBuiltSubcategoryChange,
    fetchMergedBlogs, handleReorderBlogsInMerged, handleRemoveBlogFromMerged,
    isAuthenticated, user,
    showSnackbar, reloadMergedBlog,
    setPausedBlogs,
    setMergedBlogsStartDate, setMergedBlogsEndDate,
    allTags, blogActions,
  } = useBlogContext();

  return (
    <Popover
      open={dateRangePickerOpen}
      anchorEl={dateRangePickerAnchor}
      onClose={() => setDateRangePickerOpen(false)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: { p: 2, minWidth: 700 }
      }}
    >
      <Box>
        {/* Header with Include Today and Quick Select */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 1.5, bgcolor: '#ffeb3b', borderRadius: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeToday}
                onChange={(e) => setIncludeToday(e.target.checked)}
                size="small"
              />
            }
            label="Include today"
            sx={{ m: 0 }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const today = new Date();
              const startOfYear = new Date(today.getFullYear(), 0, 1);
              setTempStartDate(formatDateForInput(startOfYear));
              setTempEndDate(formatDateForInput(today));
              setStartMonth(startOfYear);
              setEndMonth(today);
            }}
            sx={{ bgcolor: '#fff', borderColor: '#000', '&:hover': { bgcolor: '#f5f5f5' } }}
          >
            This year to date
            <KeyboardArrowDown sx={{ ml: 0.5 }} />
          </Button>
        </Box>
    
        {/* Two Calendar Grids */}
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Start Date Calendar */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Start Date</Typography>
            {(() => {
              const daysInMonth = getDaysInMonth(startMonth);
              const firstDay = getFirstDayOfMonth(startMonth);
              const days = [];
              const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    
              for (let i = 0; i < firstDay; i++) {
                days.push(null);
              }
    
              for (let i = 1; i <= daysInMonth; i++) {
                days.push(i);
              }
    
              return (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {monthNames[startMonth.getMonth()]} {startMonth.getFullYear()}
                    </Typography>
                    <Box>
                      <IconButton size="small" onClick={() => {
                        const newMonth = new Date(startMonth);
                        newMonth.setMonth(newMonth.getMonth() - 1);
                        setStartMonth(newMonth);
                      }}>
                        <ChevronLeft fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => {
                        const newMonth = new Date(startMonth);
                        newMonth.setMonth(newMonth.getMonth() + 1);
                        setStartMonth(newMonth);
                      }}>
                        <ChevronRight fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                      <Typography key={day} variant="caption" sx={{ textAlign: 'center', fontWeight: 'bold', p: 0.5 }}>
                        {day}
                      </Typography>
                    ))}
                    {days.map((day, idx) => {
                      if (day === null) {
                        return <Box key={idx} sx={{ height: 32 }} />;
                      }
                      const dateStr = formatDateForInput(new Date(startMonth.getFullYear(), startMonth.getMonth(), day));
                      const isSelected = tempStartDate && isSameDay(dateStr, tempStartDate);
                      const isInRange = tempStartDate && tempEndDate && isDateInRange(dateStr, tempStartDate, tempEndDate);
    
                      return (
                        <Button
                          key={idx}
                          size="small"
                          onClick={() => {
                            if (!tempStartDate || (tempStartDate && tempEndDate)) {
                              setTempStartDate(dateStr);
                              setTempEndDate(null);
                            } else if (tempStartDate && !tempEndDate) {
                              if (new Date(dateStr) >= new Date(tempStartDate)) {
                                setTempEndDate(dateStr);
                              } else {
                                setTempEndDate(tempStartDate);
                                setTempStartDate(dateStr);
                              }
                            }
                          }}
                          sx={{
                            minWidth: 32,
                            height: 32,
                            p: 0,
                            bgcolor: isSelected ? 'primary.main' : isInRange ? 'primary.light' : 'transparent',
                            color: isSelected ? 'white' : 'text.primary',
                            '&:hover': {
                              bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                            },
                          }}
                        >
                          {day}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              );
            })()}
          </Box>
    
          {/* End Date Calendar */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>End Date</Typography>
            {(() => {
              const daysInMonth = getDaysInMonth(endMonth);
              const firstDay = getFirstDayOfMonth(endMonth);
              const days = [];
              const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    
              for (let i = 0; i < firstDay; i++) {
                days.push(null);
              }
    
              for (let i = 1; i <= daysInMonth; i++) {
                days.push(i);
              }
    
              return (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {monthNames[endMonth.getMonth()]} {endMonth.getFullYear()}
                    </Typography>
                    <Box>
                      <IconButton size="small" onClick={() => {
                        const newMonth = new Date(endMonth);
                        newMonth.setMonth(newMonth.getMonth() - 1);
                        setEndMonth(newMonth);
                      }}>
                        <ChevronLeft fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => {
                        const newMonth = new Date(endMonth);
                        newMonth.setMonth(newMonth.getMonth() + 1);
                        setEndMonth(newMonth);
                      }}>
                        <ChevronRight fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                      <Typography key={day} variant="caption" sx={{ textAlign: 'center', fontWeight: 'bold', p: 0.5 }}>
                        {day}
                      </Typography>
                    ))}
                    {days.map((day, idx) => {
                      if (day === null) {
                        return <Box key={idx} sx={{ height: 32 }} />;
                      }
                      const dateStr = formatDateForInput(new Date(endMonth.getFullYear(), endMonth.getMonth(), day));
                      const isSelected = tempEndDate && isSameDay(dateStr, tempEndDate);
                      const isInRange = tempStartDate && tempEndDate && isDateInRange(dateStr, tempStartDate, tempEndDate);
    
                      return (
                        <Button
                          key={idx}
                          size="small"
                          onClick={() => {
                            if (!tempStartDate || (tempStartDate && tempEndDate)) {
                              setTempStartDate(dateStr);
                              setTempEndDate(null);
                            } else if (tempStartDate && !tempEndDate) {
                              if (new Date(dateStr) >= new Date(tempStartDate)) {
                                setTempEndDate(dateStr);
                              } else {
                                setTempEndDate(tempStartDate);
                                setTempStartDate(dateStr);
                              }
                            }
                          }}
                          sx={{
                            minWidth: 32,
                            height: 32,
                            p: 0,
                            bgcolor: isSelected ? 'primary.main' : isInRange ? 'primary.light' : 'transparent',
                            color: isSelected ? 'white' : 'text.primary',
                            '&:hover': {
                              bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                            },
                          }}
                        >
                          {day}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              );
            })()}
          </Box>
        </Box>
    
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setDateRangePickerOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              let finalStartDate = tempStartDate;
              let finalEndDate = tempEndDate;
    
              if (includeToday && finalEndDate) {
                const today = formatDateForInput(new Date());
                if (new Date(today) > new Date(finalEndDate)) {
                  finalEndDate = today;
                }
              }
    
              setMergedBlogsStartDate(finalStartDate || '');
              setMergedBlogsEndDate(finalEndDate || '');
              setDateRangePickerOpen(false);
            }}
            sx={{ bgcolor: '#ffeb3b', color: '#000', '&:hover': { bgcolor: '#fdd835' } }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default DateRangePicker;
